import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '@/components/layout/PageLayout';
import SettingCard from '@/components/settings/SettingCard';
import SettingRow from '@/components/settings/SettingRow';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Info, Check, CreditCard, ArrowRight, X, Plus, Trash2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { toast } from '@/components/ui/sonner';
import { teamService, Team } from '@/services/teamService';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { Textarea } from '@/components/ui/textarea';

const Settings = () => {
  const { canAccessSettings, isViewer } = useRoleAccess();
  
  const [team, setTeam] = useState<Team | null>(null);
  const [userRole, setUserRole] = useState<string>('viewer');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state (these will sync with team data)
  const [orgName, setOrgName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [retentionPolicy, setRetentionPolicy] = useState('30d');
  const [usageAlerts, setUsageAlerts] = useState(true);
  const [weeklySummary, setWeeklySummary] = useState(false);
  const [complianceMode, setComplianceMode] = useState(false);
  const [autoTTLPurge, setAutoTTLPurge] = useState(true);
  const [tokenBudget, setTokenBudget] = useState('2000');
  const [semanticWeight, setSemanticWeight] = useState('70');

  // Custom Extraction state (local only for now)
  const [categories, setCategories] = useState<Array<{ name: string; examples: string }>>([
    { name: '', examples: '' }
  ]);
  const [topics, setTopics] = useState<Array<{ topic: string; subTopics: string[] }>>([
    { topic: '', subTopics: [''] }
  ]);
  const [customPrompt, setCustomPrompt] = useState<string>('');
  const [isSavingExtraction, setIsSavingExtraction] = useState(false);

  const TEAM_SETTING_KEYS = {
    categories: 'custom_extraction_categories',
    topics: 'custom_extraction_topics',
    prompt: 'custom_extraction_prompt',
  } as const;

  // Email validation state
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [hasUserEditedEmail, setHasUserEditedEmail] = useState(false);

  // Email validation function
  const validateEmail = (email: string): boolean => {
    // More strict email regex that validates proper email format
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Handlers: Custom Extraction
  const handleAddCategory = () => {
    setCategories(prev => [...prev, { name: '', examples: '' }]);
  };

  const handleRemoveCategory = (index: number) => {
    setCategories(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateCategory = (index: number, field: 'name' | 'examples', value: string) => {
    setCategories(prev => prev.map((cat, i) => (i === index ? { ...cat, [field]: value } : cat)));
  };

  const handleAddTopic = () => {
    setTopics(prev => [...prev, { topic: '', subTopics: [''] }]);
  };

  const handleRemoveTopic = (index: number) => {
    setTopics(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateTopicName = (index: number, value: string) => {
    setTopics(prev => prev.map((t, i) => (i === index ? { ...t, topic: value } : t)));
  };

  const handleAddSubTopic = (topicIndex: number) => {
    setTopics(prev => prev.map((t, i) => (i === topicIndex ? { ...t, subTopics: [...t.subTopics, ''] } : t)));
  };

  const handleRemoveSubTopic = (topicIndex: number, subIndex: number) => {
    setTopics(prev => prev.map((t, i) => (
      i === topicIndex ? { ...t, subTopics: t.subTopics.filter((_, si) => si !== subIndex) } : t
    )));
  };

  const handleUpdateSubTopic = (topicIndex: number, subIndex: number, value: string) => {
    setTopics(prev => prev.map((t, i) => (
      i === topicIndex
        ? { ...t, subTopics: t.subTopics.map((s, si) => (si === subIndex ? value : s)) }
        : t
    )));
  };

  const handleSaveCustomExtraction = () => {
    if (userRole !== 'admin') {
      toast.error('Permission denied', {
        description: 'Only team admins can update custom extraction settings'
      });
      return;
    }

    try {
      setIsSavingExtraction(true);

      // Normalize categories: split examples by comma and trim
      const normalizedCategories = categories
        .filter(c => c.name.trim() || c.examples.trim())
        .map(c => ({
          name: c.name.trim(),
          examples: c.examples
            .split(',')
            .map(e => e.trim())
            .filter(Boolean),
        }));

      // Normalize topics: ensure subTopics trimmed, non-empty
      const normalizedTopics = topics
        .filter(t => t.topic.trim() || (t.subTopics || []).some(s => s.trim()))
        .map(t => ({
          topic: t.topic.trim(),
          sub_topics: (t.subTopics || []).map(s => s.trim()).filter(Boolean),
        }));

      const payload = [
        { type: TEAM_SETTING_KEYS.categories, content: JSON.stringify(normalizedCategories) },
        { type: TEAM_SETTING_KEYS.topics, content: JSON.stringify(normalizedTopics) },
        { type: TEAM_SETTING_KEYS.prompt, content: customPrompt || '' },
      ];

      teamService.updateTeamSettings(payload);

      toast.success('Custom extraction saved', {
        description: 'Your extraction configuration has been updated'
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save custom extraction settings';
      toast.error('Error saving', { description: errorMessage });
    } finally {
      setIsSavingExtraction(false);
    }
  };

  const loadTeamSettings = async () => {
    try {
      const settings = await teamService.getTeamSettings();
      const map = new Map<string, string>();
      settings.forEach(s => map.set(s.type, s.content));

      // Categories
      const categoriesRaw = map.get(TEAM_SETTING_KEYS.categories) || '';
      if (categoriesRaw) {
        try {
          const parsed = JSON.parse(categoriesRaw) as Array<{ name?: string; examples?: string[] | string }>;
          if (Array.isArray(parsed)) {
            setCategories(parsed.map(item => ({
              name: (item.name || '').toString(),
              examples: Array.isArray(item.examples)
                ? item.examples.join(', ')
                : (item.examples || '').toString()
            })));
          }
        } catch {
          // leave defaults if parsing fails
        }
      }

      // Topics
      const topicsRaw = map.get(TEAM_SETTING_KEYS.topics) || '';
      if (topicsRaw) {
        try {
          const parsed = JSON.parse(topicsRaw) as Array<{ topic?: string; sub_topics?: string[]; subTopics?: string[] }>;
          if (Array.isArray(parsed)) {
            setTopics(parsed.map(item => ({
              topic: (item.topic || '').toString(),
              subTopics: (item.subTopics || item.sub_topics || []).map(s => s.toString())
            })));
          }
        } catch {
          // ignore parse error
        }
      }

      // Prompt
      const promptRaw = map.get(TEAM_SETTING_KEYS.prompt) || '';
      setCustomPrompt(promptRaw);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load team settings';
      toast.error('Error loading custom extraction', { description: errorMessage });
    }
  };

  // Handle email change with validation
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setAdminEmail(email);
    setHasUserEditedEmail(true);
    
    if (email === '') {
      setEmailError(null);
      setIsEmailValid(true);
    } else if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      setIsEmailValid(false);
    } else {
      setEmailError(null);
      setIsEmailValid(true);
    }
  };

  // Load team data on component mount
  useEffect(() => {
    loadTeamData();
  }, []);

  // Load team settings on mount
  useEffect(() => {
    loadTeamSettings();
    // We deliberately load once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync form state with team data when team loads
  useEffect(() => {
    if (team) {
      setOrgName(team.name);
      setAdminEmail(team.adminEmail);
      setRetentionPolicy(team.dataRetentionPolicy);
      setComplianceMode(team.complianceMode);
      setAutoTTLPurge(team.autoPurgeExpired);
      setTokenBudget(team.defaultTokenBudget.toString());
      setSemanticWeight(team.defaultSemanticWeight.toString());
      
      // Reset user interaction state when loading from server
      setHasUserEditedEmail(false);
      setEmailError(null);
      setIsEmailValid(true);
    }
  }, [team]);

  const loadTeamData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const [teamData, profileData] = await Promise.all([
        teamService.getTeam(),
        teamService.getUserProfile()
      ]);
      
      setTeam(teamData);
      setUserRole(profileData.teamRole);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load team settings';
      setError(errorMessage);
      toast.error('Error loading settings', {
        description: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveOrg = async () => {
    if (userRole !== 'admin') {
      toast.error('Permission denied', {
        description: 'Only team admins can update organization settings'
      });
      return;
    }

    // Validate email before saving
    if (adminEmail && !validateEmail(adminEmail)) {
      toast.error('Invalid email format', {
        description: 'Please enter a valid email address'
      });
      return;
    }

    try {
      setIsSaving(true);
      
      const updatedTeam = await teamService.updateTeam({
        name: orgName,
        adminEmail: adminEmail,
        dataRetentionPolicy: retentionPolicy,
        autoPurgeExpired: autoTTLPurge,
        defaultTokenBudget: parseInt(tokenBudget),
        defaultSemanticWeight: parseInt(semanticWeight),
        complianceMode: complianceMode,
      });

      setTeam(updatedTeam);
      
      toast.success("Settings saved", {
        description: "Your organization settings have been updated"
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save settings';
      toast.error('Error saving settings', {
        description: errorMessage
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleEnableCompliance = async (value: boolean) => {
    if (userRole !== 'admin') {
      toast.error('Permission denied', {
        description: 'Only team admins can change compliance settings'
      });
      return;
    }

    setComplianceMode(value);
    if (value) {
      setRetentionPolicy('30d');
      setAutoTTLPurge(true);
      toast("Compliance Mode Enabled", {
        description: "Default TTL and purge settings have been updated to comply with regulations."
      });
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <PageLayout 
        title="Settings" 
        description="Manage your organization and notification preferences"
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading settings...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Show error state
  if (error && !team) {
    return (
      <PageLayout 
        title="Settings" 
        description="Manage your organization and notification preferences"
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-500 mb-4">Error: {error}</p>
            <Button onClick={loadTeamData} variant="outline">
              Try Again
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const canEdit = canAccessSettings;
  
  return (
    <PageLayout 
      title="Settings" 
      description="Manage your organization and notification preferences"
    >
      <div>
        {!canEdit && (
          <div className="mb-6 p-4 bg-yellow-500/10 rounded-lg flex gap-3 items-start border border-yellow-500/30">
            <Info className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm">View Only Mode</p>
              <p className="text-xs text-muted-foreground mt-1">Only team administrators can modify organization settings.</p>
            </div>
          </div>
        )}

        <SettingCard 
          title="Organization Settings" 
          description="Configure your organization details and defaults"
        >
          <SettingRow 
            title="Organization Name" 
            description="The name of your organization as it appears across the platform"
          >
            <div className="w-full sm:w-72">
              <Input 
                value={orgName} 
                onChange={(e) => setOrgName(e.target.value)} 
                className="bg-recallio-medium-gray border-recallio-light-gray/50"
                disabled={!canEdit}
              />
            </div>
          </SettingRow>
          
          <SettingRow 
            title="Admin Email" 
            description="The email address for administrative notifications and alerts"
          >
            <div className="w-full sm:w-72">
              <div className="relative">
                <Input 
                  type="email" 
                  value={adminEmail} 
                  onChange={handleEmailChange}
                  className={`bg-recallio-medium-gray border-recallio-light-gray/50 pr-8 ${
                    hasUserEditedEmail && adminEmail && !isEmailValid 
                      ? 'border-red-500 focus:border-red-500' 
                      : hasUserEditedEmail && adminEmail && isEmailValid 
                      ? 'border-green-500 focus:border-green-500' 
                      : ''
                  }`}
                  disabled={!canEdit}
                  placeholder="admin@example.com"
                />
                {hasUserEditedEmail && adminEmail && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {isEmailValid ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <X className="h-4 w-4 text-red-500" />
                    )}
                  </div>
                )}
              </div>
              {hasUserEditedEmail && emailError && (
                <p className="text-red-500 text-xs mt-1">{emailError}</p>
              )}
            </div>
          </SettingRow>
          
          {canEdit && (
            <div className="mt-4">
              <Button 
                onClick={handleSaveOrg} 
                className="btn-primary"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </SettingCard>
        
        <SettingCard 
          title="Notification Settings" 
          description="Configure how and when you receive notifications"
        >
          <SettingRow 
            title="Usage Alerts" 
            description="Get notified when you reach 80% of your usage limits"
          >
            <Switch 
              checked={usageAlerts}
              onCheckedChange={setUsageAlerts}
            />
          </SettingRow>
        </SettingCard>
        
        <SettingCard title="Billing">
          <div className="py-6 px-8 bg-recallio-medium-gray/50 rounded-lg border border-dashed border-recallio-light-gray/50">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-medium mb-1">Manage your subscription</h3>
                <p className="text-muted-foreground text-sm">View payment history, update your plan, and manage your billing details</p>
              </div>
              <Button asChild>
                <Link to="/billing" className="flex items-center">
                  <CreditCard size={16} className="mr-2" />
                  Billing Portal
                  <ArrowRight size={14} className="ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </SettingCard>

        <SettingCard 
          title="Help Recallio understand your product" 
          description="By telling us what your app does and the topics it covers, we’ll tailor memory"
        >
          <SettingRow 
            title="Categories" 
            description="Provide a category name and example items (comma-separated). Example: Projects: to-dos, milestones, deadlines, status updates"
            stacked
          >
            <div className="w-full space-y-4">
              {categories.map((cat, index) => (
                <div key={index} className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-start">
                  <div className="sm:col-span-2">
                    <Input 
                      placeholder="Category name (e.g., Projects)" 
                      value={cat.name}
                      onChange={(e) => handleUpdateCategory(index, 'name', e.target.value)}
                      className="bg-recallio-medium-gray border-recallio-light-gray/50"
                      disabled={!canEdit}
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <Input 
                      placeholder="Examples (e.g., to-dos, milestones, deadlines, status updates)" 
                      value={cat.examples}
                      onChange={(e) => handleUpdateCategory(index, 'examples', e.target.value)}
                      className="bg-recallio-medium-gray border-recallio-light-gray/50"
                      disabled={!canEdit}
                    />
                  </div>
                  {canEdit && (
                    <div className="sm:col-span-5 flex justify-end">
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleRemoveCategory(index)}
                        disabled={categories.length === 1}
                      >
                        <Trash2 size={16} className="mr-1" /> Remove
                      </Button>
                    </div>
                  )}
                </div>
              ))}

              {canEdit && (
                <Button type="button" variant="outline" size="sm" onClick={handleAddCategory}>
                  <Plus size={16} className="mr-1" /> Add Category
                </Button>
              )}
            </div>
          </SettingRow>

          <SettingRow 
            title="Topics & Sub-Topics" 
            description='Define topics and their sub-topics. Example: contact_info: ["email", "phone", "city", "country"]'
            stacked
          >
            <div className="w-full space-y-6">
              {topics.map((t, ti) => (
                <div key={ti} className="rounded-md border border-recallio-light-gray/50 p-4 bg-recallio-medium-gray/30">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-start">
                    <div className="sm:col-span-2">
                      <Input 
                        placeholder="Topic (e.g., contact_info)" 
                        value={t.topic}
                        onChange={(e) => handleUpdateTopicName(ti, e.target.value)}
                        className="bg-recallio-medium-gray border-recallio-light-gray/50"
                        disabled={!canEdit}
                      />
                    </div>
                    {canEdit && (
                      <div className="sm:col-span-1 flex sm:justify-end">
                        <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveTopic(ti)} disabled={topics.length === 1}>
                          <Trash2 size={16} className="mr-1" /> Remove Topic
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 space-y-3">
                    {t.subTopics.map((s, si) => (
                      <div key={si} className="grid grid-cols-1 sm:grid-cols-6 gap-3 items-start">
                        <div className="sm:col-span-5">
                          <Input 
                            placeholder="Sub-topic (e.g., email)" 
                            value={s}
                            onChange={(e) => handleUpdateSubTopic(ti, si, e.target.value)}
                            className="bg-recallio-medium-gray border-recallio-light-gray/50"
                            disabled={!canEdit}
                          />
                        </div>
                        {canEdit && (
                          <div className="sm:col-span-1 flex sm:justify-end">
                            <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveSubTopic(ti, si)} disabled={t.subTopics.length === 1}>
                              <Trash2 size={16} className="mr-1" /> Remove
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}

                    {canEdit && (
                      <Button type="button" variant="outline" size="sm" onClick={() => handleAddSubTopic(ti)}>
                        <Plus size={16} className="mr-1" /> Add Sub-Topic
                      </Button>
                    )}
                  </div>
                </div>
              ))}

              {canEdit && (
                <Button type="button" variant="outline" size="sm" onClick={handleAddTopic}>
                  <Plus size={16} className="mr-1" /> Add Topic
                </Button>
              )}
            </div>
          </SettingRow>

          <SettingRow 
            title="Custom extraction prompt" 
            description="Provide detailed guidance for how to extract and organize information"
            stacked
          >
            <div className="w-full">
              <Textarea 
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                className="bg-recallio-medium-gray border-recallio-light-gray/50 min-h-[200px]"
                placeholder={
`1. Store Personal Preferences: Keep track of likes, dislikes, and specific preferences in various categories such as food, products, activities, and entertainment.
2. Maintain Important Personal Details: Remember significant personal information like names, relationships, and important dates.
3. Track Plans and Intentions: Note upcoming events, trips, goals, and any plans the user has shared.
4. Remember Activity and Service Preferences: Recall preferences for dining, travel, hobbies, and other services.
5. Monitor Health and Wellness Preferences: Keep a record of dietary restrictions, fitness routines, and other wellness-related information.
6. Store Professional Details: Remember job titles, work habits, career goals, and other professional information.
7. Miscellaneous Information Management: Keep track of favorite books, movies, brands, and other miscellaneous details that the user shares.`
                }
                disabled={!canEdit}
              />
            </div>
          </SettingRow>

          {canEdit && (
            <div className="mt-4">
              <Button onClick={handleSaveCustomExtraction} className="btn-primary" disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Custom Extraction'}
              </Button>
            </div>
          )}
        </SettingCard>
      </div>
    </PageLayout>
  );
};

export default Settings;
